import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../config/firebase";
import roleService from "./roleService";
import {
  ADMIN,
  CSF,
  SUPER_ADMIN,
  DIVISION_CONTROLLER,
  SALES_SUPERVISOR,
  TEAM_MEMBER,
  EXCEPTIONAL_ADMIN
} from "../constants/roles";

const usersCollection = collection(db, "users");

const userService = {
  getAll: async () => {
    try {
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return usersList;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  },
  add: async (usersData) => {
    try {
      const docRef = await addDoc(usersCollection, usersData);
      return docRef;
    } catch (error) {
      console.error("Error adding user:", error);
      throw error;
    }
  },
  edit: async ({ id, data }) => {
    try {
      const userRef = doc(db, "users", id);
      const snapshot = await getDoc(userRef);
      if (!snapshot.exists()) {
        throw new Error("User not found");
      }
      await updateDoc(userRef, data);
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },
  delete: async (userId) => {
    try {
      await deleteDoc(doc(db, "users", userId));
      return true;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },

  addWithUUID: async ({ userId, userData }) => {
    try {
      const userRef = doc(db, "users", userId);
      await setDoc(userRef, userData);
    } catch (error) {
      console.error("Error adding user with UUID:", error);
      throw error;
    }
  },

  fetchUsersByRole: async ({ user }) => {
    if (!user?.uid || !user?.role_id) return [];

    try {
      const roleName = await roleService.getNameById(user.role_id);
      if (!roleName) return [];

      let q;
      switch (roleName) {
        case ADMIN:
        case CSF:
        case SUPER_ADMIN:
        case EXCEPTIONAL_ADMIN:
          q = query(collection(db, "users"));
          break;

        case DIVISION_CONTROLLER:
          q = query(
            collection(db, "users"),
            where("division_id", "==", user.division_id)
          );
          break;

        case SALES_SUPERVISOR:
          q = query(
            collection(db, "users"),
            where("team_id", "==", user.team_id)
          );
          break;

        case TEAM_MEMBER:
        default:
          return [];
      }

      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      return users.filter((userData) => userData.id !== user.uid);
    } catch (error) {
      console.error("Error fetching users by role:", error);
      return [];
    }
  },

  getUserDivisions: async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userSnapshot = await getDoc(userRef);
      if (!userSnapshot.exists()) return [];

      const userData = userSnapshot.data();
      const roleName = await roleService.getNameById(userData.role_id);

      // Show all divisions for admin/CFS or when division_id is empty
      if (
        roleName === ADMIN ||
        roleName === CSF ||
        roleName === SUPER_ADMIN ||
        !userData.division_id
      ) {
        const divisionsCollection = collection(db, "divisions");
        const snapshot = await getDocs(divisionsCollection);
        return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      }

      // For other roles with division_id, show only their division
      const divisionsCollection = collection(db, "divisions");
      const divisionRef = doc(divisionsCollection, userData.division_id);
      const divisionSnapshot = await getDoc(divisionRef);
      return divisionSnapshot.exists()
        ? [{ id: divisionSnapshot.id, ...divisionSnapshot.data() }]
        : [];
    } catch (error) {
      console.error("Error fetching user divisions:", error);
      return [];
    }
  },

  toggleUserStatus: async ({ userId, currentStatus }) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        status: !currentStatus,
      });
      return true;
    } catch (error) {
      console.error("Error toggling user status:", error);
      throw error;
    }
  },

  checkDuplicates: async ({ UPI_num, email_address }) => {
    try {
      // Check for duplicate UPI
      const upiQuery = query(usersCollection, where("UPI_num", "==", UPI_num));
      const upiSnapshot = await getDocs(upiQuery);

      // Check for duplicate email
      const emailQuery = query(
        usersCollection,
        where("email_address", "==", email_address.toLowerCase())
      );
      const emailSnapshot = await getDocs(emailQuery);

      const duplicates = {
        UPI_num: !upiSnapshot.empty,
        email_address: !emailSnapshot.empty,
      };

      return duplicates;
    } catch (error) {
      console.error("Error checking duplicates:", error);
      throw error;
    }
  },

  getUserByEmail: async (email) => {
    try {
      const userRef = query(
        usersCollection,
        where("email_address", "==", email)
      );
      const snapshot = await getDocs(userRef);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error fetching user by email:", error);
      throw error;
    }
  },
  generateCustomToken: async (email) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/generate-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email }),
        }
      );
      return res.json();
    } catch (error) {
      console.error("Error generating custom token:", error);
      throw error;
    }
  },
  getFirstNameFamilyNameById: async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      const userSnapshot = await getDoc(userRef);
      if (!userSnapshot.exists()) return [];

      const userData = userSnapshot.data();
      return `${userData.first_name} ${userData.family_name}`;
    } catch (error) {
      console.error("Error fetching user name:", error);
      return [];
    }
  },
};

export default userService;
